import { useEffect, useMemo, useState } from "react";
import BundleFiltersContext from "./BundleFiltersContext";
import { getBundleOptions } from "modules/admin/OrgProductLicenseForm/utilities";
import { PRODUCTS } from "modules/admin/OrgProductLicenseForm";
import { PRODUCT_BUNDLES } from "modules/admin/OrgProductLicenseForm/constants";
import Loader from "core/components/Loader";

const BundleFiltersProvider = ({ customerSegment, children }) => {
  const [areAllBundlesSelected, setAreAllBundlesSelected] = useState(true);

  const [bundleOptions, setBundleOptions] = useState(() => {
    return getBundleOptions();
  });

  const bundleOptionValues = useMemo(() => {
    if (customerSegment) {
      return bundleOptions.map((bundleOption) => {
        const bundleId = bundleOption.id;
        const bundleProductIds = Object.values(PRODUCTS)
          .filter((product) => {
            return product.meta.bundle.id === bundleId;
          })
          .map((product) => product.id);
        const isBundleLicensable = customerSegment.licensableProductIds.some(
          (licensableProductId) =>
            bundleProductIds.includes(licensableProductId)
        );

        return {
          ...bundleOption,
          isDisabled: !isBundleLicensable,
          value: isBundleLicensable ? bundleOption.value : "false",
        };
      });
    }

    return bundleOptions;
  }, [customerSegment, bundleOptions]);

  const selectedBundles = useMemo(() => {
    const selectedBundleIds = bundleOptions
      .filter((bov) => bov.value === "true")
      .map((bov) => bov.id);

    return selectedBundleIds
      .filter((bundleId) => {
        if (customerSegment) {
          const bundleProductIds = Object.values(PRODUCTS)
            .filter((product) => {
              return product.meta.bundle.id === bundleId;
            })
            .map((product) => product.id);

          // Return selected bunbdles that have at least 1 licensable product under the current customer segment
          return customerSegment.licensableProductIds.some(
            (licensableProductId) =>
              bundleProductIds.includes(licensableProductId)
          );
        }

        return true;
      })
      .map((bundleId) => PRODUCT_BUNDLES[bundleId]);
  }, [customerSegment, bundleOptions]);

  const selectedBundleIds = useMemo(() => {
    return selectedBundles.map((selectedBundle) => selectedBundle.id);
  }, [selectedBundles]);

  const visibleProducts = useMemo(() => {
    return Object.values(PRODUCTS)
      .filter((product) => {
        if (customerSegment) {
          return customerSegment.licensableProductIds.includes(product.id);
        }
        return true;
      })
      .filter((product) => selectedBundleIds.includes(product.meta.bundle.id));
  }, [customerSegment, selectedBundleIds]);

  const visibleProductIds = useMemo(() => {
    return visibleProducts.map((product) => product.id);
  }, [visibleProducts]);

  const toggleAllBundles = () => {
    setAreAllBundlesSelected((areAllPreviousSelected) => {
      setBundleOptions((previousOptionValues) => {
        return previousOptionValues.map((optionValue) => {
          return {
            ...optionValue,
            value: areAllPreviousSelected ? "false" : "true",
          };
        });
      });

      return !areAllPreviousSelected;
    });
  };

  const updateBundleOptions = (updatedOptions) => {
    const areAllSelected = updatedOptions
      .filter((option) => option.isDisabled === false)
      .every((option) => option.value === "true");
    setBundleOptions(updatedOptions);
    setAreAllBundlesSelected(areAllSelected);
  };

  return (
    <BundleFiltersContext.Provider
      value={{
        areAllBundlesSelected,
        toggleAllBundles,
        bundleOptionValues,
        updateBundleOptions,
        selectedBundles,
        selectedBundleIds,
        visibleProducts,
        visibleProductIds,
      }}
    >
      {customerSegment ? (
        children
      ) : (
        <Loader
          style={{
            paddingBottom: "20vh",
          }}
        />
      )}
    </BundleFiltersContext.Provider>
  );
};

export default BundleFiltersProvider;
